// ponytail: copy of form/orders.js (bulk pattern) for corp + click-and-collect (deliveryMethodId 2) confirmation.
// Unlike the sibling forms, value/quantity/card-range are entered manually per product line (not
// auto-generated) — the collection API takes a startCardNumber/endCardNumber pair per line, and a
// line can be split into more lines ("Add another line") when a batch needs more than one range.
import React, { useContext, useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { observer, useObserver } from "mobx-react-lite";
import Moment from "react-moment";

import { Row, Col } from "reactstrap";

/* design */
import DesignSection from "../../app-design-section";
/* design */

/* packages */
import { Inputs, Buttons, Invalid } from "../../lib-atomic";
import { FormHook, LocalStorage } from "../../lib-atomic-functions";
import { FetchContextApi } from "../../lib-atomic-functions";
import {
  ModuleTitle,
  ModuleLink,
  ModuleText,
  ModuleImage,
} from "../../lib-atomic-design-section";
import { PDFBlobProviderInvoiceCorp } from "../../app-module-order-page-pdf/program";
/* packages */

/* module */
import ProductDetail from "../../app-module-product-detail/detail";
/* module */

/* stores */
import {
  OrderConfirm,
  OrderEmailInvoice,
} from "../../app-module-order-container/stores";
/* stores */

import CONFIG from "../config";

/* CONSTANTS */
const LANDINGGRAPHICS = CONFIG.setup.assets.landing.default;
const CONFIRMATION = CONFIG.application.order.api.endpoints.confirmation;
const CONFIRMATIONOBJECTS = CONFIG.application.order.api.objects;
const CONFIRMATIONCONFIG = {
  url: CONFIRMATION.url,
  method: CONFIRMATION.method,
  headers: CONFIRMATION.headers,
  responseType: "json",
};
const SENDATTACHMENT = CONFIG.application.order.api.endpoints.sendattachment;
const SENDATTACHMENTCONFIG = {
  url: SENDATTACHMENT.url,
  method: SENDATTACHMENT.method,
  headers: SENDATTACHMENT.headers,
  responseType: "formdata",
};
const CARD_TYPES = ["red", "hb", "ty", "ch", "sb", "rb"];
const CARD_LABELS = {
  red: "Default GC",
  hb: "Happy Birthday GC",
  ty: "Thank you GC",
  ch: "Christmas",
  sb: "Star Baloon",
  rb: "Red Baloon",
};
const EMPTY_EXTRA_LINES = { red: [], hb: [], ty: [], ch: [], sb: [], rb: [] };
const fieldKey = (kind, type, lineId) => `${kind}_${type}_${lineId}`;
/* CONSTANTS */

const index = ({ props, previewData, order, product_card }) => {
  const { token } = LocalStorage.getLS().login;
  const tokenCorp = LocalStorage.getLS().loginCorp.token;
  const { products } = product_card;
  const orderId = order.id;
  const isCorporate = order.flCorporate === 1 ? true : false;
  const cardProductIds = CONFIG.products.cards;
  const [confirm, setConfirm] = useState(false);
  const [topupError, setTopupError] = useState(false);
  const [topupErrorMsg, setTopupErrorMsg] = useState("");
  // extraLines: card type -> array of manually-added line ids (splits beyond the order's own lines)
  const [extraLines, setExtraLines] = useState(EMPTY_EXTRA_LINES);
  const [nextExtraId, setNextExtraId] = useState(0);

  /* states */
  CONFIRMATIONCONFIG.headers = {
    ...CONFIRMATIONCONFIG.headers,
    Authorization: isCorporate ? tokenCorp : token,
    appName: "corp",
  };
  const [orderConfirmContext, orderConfirmMethod] = FetchContextApi(
    CONFIRMATIONCONFIG,
    OrderConfirm,
  );
  const [orderEmailInvoiceContext, orderEmailInvoiceMethod] = FetchContextApi(
    SENDATTACHMENTCONFIG,
    OrderEmailInvoice,
  );
  /* states */

  async function prepareBlob(previewData) {
    return await PDFBlobProviderInvoiceCorp(previewData);
  }

  const handleGenProducts = (products) => {
    const productsCards = products.filter(
      (product) => product.subClassId === 3,
    );
    const byType = {};
    CARD_TYPES.forEach((type) => {
      byType[type] = productsCards.filter(
        (product) => product.productId === cardProductIds[type],
      );
    });
    return { productsCards, byType };
  };

  const { productsCards, byType } = handleGenProducts(products);

  const total_cards = productsCards.reduce(
    (a, b) => a + (b["quantity"] || 0),
    0,
  );
  const total_value = products
    .reduce((a, b) => a + (b["value"] * b["quantity"] || 0), 0)
    .toFixed(2);

  // Every card type's lines = its order line(s) (fixed, index-keyed) + any manually added splits.
  const getLines = (type) => {
    const original = byType[type].map((product, i) => ({
      lineId: String(i),
      product,
    }));
    const extra = extraLines[type].map((lineId) => ({
      lineId,
      product: null,
    }));
    return [...original, ...extra];
  };

  const handleAddLine = (type) => {
    const lineId = `x${nextExtraId}`;
    setNextExtraId((n) => n + 1);
    setExtraLines((prev) => ({ ...prev, [type]: [...prev[type], lineId] }));
  };

  const handleRemoveLine = (type, lineId) => {
    setExtraLines((prev) => ({
      ...prev,
      [type]: prev[type].filter((id) => id !== lineId),
    }));
  };

  let NEWCONFIRMATIONOBJECTS = { ...CONFIRMATIONOBJECTS };
  // Pre-select the order's existing payment type, if it has one, instead of leaving it blank.
  NEWCONFIRMATIONOBJECTS.payment = {
    ...NEWCONFIRMATIONOBJECTS.payment,
    initial: order.paymentType ? String(order.paymentType) : "",
  };

  /* per-line value/quantity/start/end fields, one set per line in each card type */
  CARD_TYPES.forEach((type) => {
    getLines(type).forEach(({ lineId, product }) => {
      const valueKey = fieldKey("value", type, lineId);
      const quantityKey = fieldKey("quantity", type, lineId);
      const startKey = fieldKey("starting", type, lineId);
      const endKey = fieldKey("ending", type, lineId);
      NEWCONFIRMATIONOBJECTS[valueKey] = {
        name: valueKey,
        initial: product ? String(product.value) : "",
        type: "text",
        label: `Value (${CARD_LABELS[type]})`,
        helpText: false,
        rules: ["isNotNull", "isNumber"],
      };
      NEWCONFIRMATIONOBJECTS[quantityKey] = {
        name: quantityKey,
        initial: product ? String(product.quantity) : "",
        type: "text",
        label: `Quantity (${CARD_LABELS[type]})`,
        helpText: false,
        rules: ["isNotNull", "isNumber"],
      };
      NEWCONFIRMATIONOBJECTS[startKey] = {
        name: startKey,
        initial: "",
        type: "text",
        label: `Starting card # (${CARD_LABELS[type]})`,
        helpText: false,
        rules: ["isNotNull"],
      };
      NEWCONFIRMATIONOBJECTS[endKey] = {
        name: endKey,
        initial: "",
        type: "text",
        label: `Ending card # (${CARD_LABELS[type]})`,
        helpText: false,
        rules: ["isNotNull", "isValidCardRange"],
        startField: startKey,
        quantityField: quantityKey,
      };
    });
  });
  /* per-line value/quantity/start/end fields */

  const handleConfirmation = async () => {
    const productAutomate = [];
    for (const type of CARD_TYPES) {
      const lines = getLines(type);
      if (lines.length === 0) continue;
      const orderedTotal = byType[type].reduce((a, p) => a + p.quantity, 0);
      const orderedValueTotal = byType[type].reduce(
        (a, p) => a + p.value * p.quantity,
        0,
      );
      let enteredTotal = 0;
      let enteredValueTotal = 0;
      for (const { lineId } of lines) {
        const value = values[fieldKey("value", type, lineId)];
        const quantity = values[fieldKey("quantity", type, lineId)];
        enteredTotal += Number(quantity) || 0;
        enteredValueTotal += (Number(value) || 0) * (Number(quantity) || 0);
        productAutomate.push({
          productId: cardProductIds[type],
          value,
          quantity,
          startCardNumber: values[fieldKey("starting", type, lineId)],
          endCardNumber: values[fieldKey("ending", type, lineId)],
        });
      }
      if (enteredTotal !== orderedTotal) {
        setTopupError(true);
        setTopupErrorMsg(
          `${CARD_LABELS[type]}: line quantities total ${enteredTotal}, but the order has ${orderedTotal}.`,
        );
        return;
      }
      if (Math.abs(enteredValueTotal - orderedValueTotal) > 0.001) {
        setTopupError(true);
        setTopupErrorMsg(
          `${CARD_LABELS[type]}: line values total $${enteredValueTotal.toFixed(2)}, but the order totals $${orderedValueTotal.toFixed(2)}.`,
        );
        return;
      }
    }

    const bodyData = {
      deliveryMethodId: 2,
      paymentTypeId: values.payment,
      extOrderNumber: "",
      comments: "",
      products: productAutomate,
    };

    await orderConfirmMethod({
      query: orderId,
      body: JSON.stringify(bodyData),
    });
    if (orderConfirmContext.status) {
      // ponytail: invoice email disabled for now, re-enable by restoring the
      // prepareBlob(...).then(...) block this replaced (see git history).
      // if order confirmed then send invoice in email
      // prepareBlob(previewData).then(async (blobPrepared) => {
      //   let formData = new FormData();
      //   formData.append("file", blobPrepared);
      //   const fullName =
      //     previewData.pdfPreviewData.contactInfoData.firstName +
      //     " " +
      //     previewData.pdfPreviewData.contactInfoData.lastName;
      //   const emailAddress =
      //     previewData.pdfPreviewData.companyInfoData.clients[0].emailAddress;
      //   const dataEmail = {
      //     templateId: "50e8878d-5032-4913-9e04-6d9d47dfec4c",
      //     fullName,
      //     emailAddress,
      //     params: {
      //       name: fullName,
      //       order_number: orderId,
      //     },
      //   };
      //   formData.append("data", JSON.stringify(dataEmail));
      //   await orderEmailInvoiceMethod({
      //     body: formData,
      //   });
      //
      //   props.history.push("/orders/l/success-quote-confirm/" + orderId);
      // });
      props.history.push("/orders/l/success-quote-confirm/" + orderId);
    } else {
      setTopupError(true);
      setTopupErrorMsg(
        orderConfirmContext.data.confirm.outcomeDeveloperMessage,
      );
    }
  };

  const handleCancel = async () => {
    props.history.push("/orders/detail/" + orderId);
  };

  const handleConfirm = () => {
    setConfirm(!confirm);
  };

  const { values, onChange, onPick, onSubmit, error } = FormHook.useFormHook(
    handleConfirmation,
    NEWCONFIRMATIONOBJECTS,
  );

  const bindField = (key) => ({
    ...NEWCONFIRMATIONOBJECTS[key],
    error: error[key],
    value:
      values[key] !== undefined
        ? values[key]
        : NEWCONFIRMATIONOBJECTS[key].initial,
    onChange,
  });

  const payment = bindField("payment");
  //const delivery = bindField("delivery");

  const cardRangeLoop = (type) => {
    return getLines(type).map(({ lineId, product }) => (
      <section
        key={lineId}
        className="space-standard-top space-standard-bottom"
      >
        <ProductDetail productId={cardProductIds[type]} />
        <Inputs.Text {...bindField(fieldKey("value", type, lineId))} />
        <Inputs.Text {...bindField(fieldKey("quantity", type, lineId))} />
        <Inputs.Text {...bindField(fieldKey("starting", type, lineId))} />
        <Inputs.Text {...bindField(fieldKey("ending", type, lineId))} />
        {!product && (
          <Buttons.Text
            size="small"
            onClick={() => handleRemoveLine(type, lineId)}
          >
            Remove line
          </Buttons.Text>
        )}
      </section>
    ));
  };

  /* data props */
  const submitButton = { size: "small" };
  const cancelButton = { size: "small", onClick: handleCancel };
  const cardRangeInputs = CARD_TYPES.map((type) => (
    <Fragment key={type}>
      {cardRangeLoop(type)}
      {byType[type].length > 0 && (
        <Buttons.Text size="small" onClick={() => handleAddLine(type)}>
          + Add another {CARD_LABELS[type]} line
        </Buttons.Text>
      )}
    </Fragment>
  ));

  const failTopup = { error: [topupErrorMsg] };
  /* data props */

  return (
    <form onSubmit={onSubmit} noValidate>
      <img src={LANDINGGRAPHICS} width="100%" />
      <ModuleTitle>Almost there!</ModuleTitle>
      <ModuleText>
        You are about to confirm order number <b>{orderId}</b>.
      </ModuleText>
      <br />
      {order.paymentType ? (
        <ModuleText>
          <b>Payment Details:</b> {order.paymentTypeName}
        </ModuleText>
      ) : (
        <Inputs.Select {...payment} />
      )}
      <hr />
      <ModuleText>
        <small>
          <i>
            * Please specify the value, quantity, and starting/ending card
            number for each line. Use "Add another line" to split a card type
            across multiple ranges.
          </i>
        </small>
      </ModuleText>
      {topupError && (
        <Fragment>
          <Invalid {...failTopup} />
          <br />
        </Fragment>
      )}
      <hr />
      {cardRangeInputs}
      {!confirm && (
        <section className="space-standard-top space-standard-bottom">
          <Buttons.Primary onClick={handleConfirm}>
            Confirm & Submit
          </Buttons.Primary>
        </section>
      )}
      {confirm && (
        <section className="space-standard-top space-standard-bottom">
          <p>
            <b>
              You are about to top up {total_cards} cards totalling $
              {total_value} do you wish to continue
            </b>
          </p>
          <Buttons.Normal {...submitButton}>
            Continue and Confirm
          </Buttons.Normal>
          <Buttons.Text {...cancelButton}>Cancel</Buttons.Text>
        </section>
      )}
    </form>
  );
};

export default index;
